import React, { useEffect, useState } from 'react';
import { authAxios, API } from '../utils/authAxios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Loader2, RefreshCw, TrendingUp, TrendingDown, Minus,
  Send, Calculator, Flame, CheckCircle2, MessageSquareHeart, CalendarDays, UserPlus, Mail,
  ArrowRight, Crown, Star, ExternalLink, BookOpen,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend, CartesianGrid,
} from 'recharts';

const PERIODS = [
  { value: 'today', label: 'Hoy' },
  { value: 'yesterday', label: 'Ayer' },
  { value: '7d', label: '7 días' },
  { value: '30d', label: '30 días' },
  { value: 'prev_month', label: 'Mes anterior' },
];

const ICON_MAP = { Send, Calculator, Flame, CheckCircle2, MessageSquareHeart, CalendarDays, UserPlus, Mail };
const PIE_COLORS = ['#10b981', '#14b8a6', '#0ea5e9', '#a855f7', '#f59e0b', '#ef4444', '#64748b'];

function Delta({ delta }) {
  if (!delta || delta.pct === null) {
    return <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5"><Minus className="w-2.5 h-2.5" /> {delta?.abs >= 0 ? '+' : ''}{delta?.abs || 0}</span>;
  }
  const positive = delta.abs >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`text-[10px] inline-flex items-center gap-0.5 ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
      <Icon className="w-2.5 h-2.5" /> {positive ? '+' : ''}{delta.abs} ({positive ? '+' : ''}{delta.pct}%)
    </span>
  );
}

function KPICard({ k }) {
  const Icon = ICON_MAP[k.icon] || Send;
  return (
    <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl hover:border-emerald-500/30 transition-colors" data-testid={`vamosya-kpi-${k.key}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-4 h-4 text-emerald-400" />
        <Delta delta={k.delta} />
      </div>
      <div className="text-2xl font-bold text-slate-50">{(k.value || 0).toLocaleString('es-CL')}</div>
      <div className="text-[11px] text-slate-400 mt-0.5">{k.label}</div>
    </div>
  );
}

export default function AdminVamosYa() {
  const [period, setPeriod] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true); setError(null);
      try {
        const res = await authAxios.get(`${API}/admin/effectiveness/dashboard?period=${period}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [period]);

  return (
    <div className="space-y-6" data-testid="admin-vamosya-dashboard">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-800">
        <img
          src={`${API.replace(/\/api$/, '')}/api/admin/effectiveness/hero-image`}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30 grayscale"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30 pointer-events-none" />
        <div className="relative p-7 flex items-center justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300 font-bold mb-2">Dashboard de Efectividad</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-50 leading-tight">¡Vamos Ya!</h2>
            <p className="text-sm text-slate-400 mt-1.5 max-w-xl">Métricas en vivo · comparativa vs período anterior equivalente.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-slate-900/70 p-1 rounded-lg border border-slate-800">
              {PERIODS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${period === p.value ? 'bg-emerald-500 text-slate-950' : 'text-slate-300 hover:bg-slate-800'}`}
                  data-testid={`vamosya-period-${p.value}`}
                >{p.label}</button>
              ))}
            </div>
            <Button onClick={() => setPeriod((v) => v)} variant="outline" size="sm" disabled={loading} data-testid="vamosya-refresh">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </Button>
          </div>
        </div>
      </div>

      {error && <p className="text-red-400 text-sm p-3 bg-red-500/10 border border-red-500/30 rounded">{error}</p>}
      {loading && !data && <div className="text-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Cargando…</div>}

      {data && (
        <>
          {/* SECCIÓN 1 — KPIs */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">1 · KPIs principales</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.kpis.map((k) => <KPICard key={k.key} k={k} />)}
            </div>
          </section>

          {/* SECCIÓN 2 — Tráfico */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">2 · Tráfico y conversión</h3>
            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Visitas únicas por día — {data.traffic.visits_total.toLocaleString('es-CL')} <Delta delta={data.traffic.visits_delta} /></CardTitle></CardHeader>
                <CardContent className="pt-0">
                  <div style={{ height: 220 }}>
                    <ResponsiveContainer>
                      <LineChart data={data.traffic.visits_by_day}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                        <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                        <YAxis stroke="#64748b" fontSize={11} />
                        <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', fontSize: 12 }} />
                        <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Top fuentes</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-1.5">
                  {data.traffic.top_sources.slice(0, 8).map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-slate-300">{s.source}</span>
                      <span className="text-emerald-400 font-mono">{s.count}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mt-3">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Top páginas vistas</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-1.5">
                  {data.traffic.top_pages.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-xs gap-2">
                      <span className="text-slate-400 truncate">{p.page}</span>
                      <span className="text-emerald-400 font-mono shrink-0">{p.count}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Funnel principal</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-2.5">
                  {[
                    { label: 'Visitas', value: data.traffic.funnel.visits },
                    { label: 'Calculadora', value: data.traffic.funnel.calc_leads },
                    { label: 'Cotizaciones', value: data.traffic.funnel.quotes },
                    { label: 'Asesorías', value: data.traffic.funnel.asesorias },
                  ].map((step, i, arr) => {
                    const max = Math.max(...arr.map((x) => x.value || 1));
                    const pct = (step.value / max) * 100;
                    const conv = i > 0 && arr[i - 1].value > 0 ? Math.round(100 * step.value / arr[i - 1].value) : null;
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-[11px] text-slate-300 mb-0.5">
                          <span>{step.label} {conv !== null && <span className="text-slate-500">· {conv}%</span>}</span>
                          <span className="font-mono text-emerald-400">{step.value}</span>
                        </div>
                        <div className="h-2 bg-slate-800 rounded">
                          <div className="h-2 bg-emerald-500 rounded" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">404s detectados · {data.traffic.page404_total}</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-1.5 text-xs">
                  {data.traffic.page404_top.length === 0 ? <p className="text-slate-500">Sin 404 en el período.</p> :
                    data.traffic.page404_top.map((p, i) => (
                      <div key={i} className="flex items-center justify-between gap-2">
                        <span className="text-amber-300 truncate">{p.page}</span>
                        <span className="text-amber-400 font-mono shrink-0">{p.count}</span>
                      </div>
                    ))
                  }
                </CardContent>
              </Card>
            </div>

            {data.traffic.utm_top.length > 0 && (
              <Card className="bg-slate-900 border-slate-800 mt-3">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Top campañas UTM</CardTitle></CardHeader>
                <CardContent className="pt-0">
                  <table className="w-full text-xs">
                    <thead><tr className="text-slate-500 border-b border-slate-800">
                      <th className="text-left p-1.5">Source</th><th className="text-left p-1.5">Medium</th><th className="text-left p-1.5">Campaña</th><th className="text-right p-1.5">Hits</th>
                    </tr></thead>
                    <tbody>
                      {data.traffic.utm_top.map((u, i) => (
                        <tr key={i} className="text-slate-300 border-b border-slate-900">
                          <td className="p-1.5">{u.source}</td><td className="p-1.5 text-slate-400">{u.medium || '—'}</td><td className="p-1.5 text-slate-400">{u.campaign || '—'}</td><td className="p-1.5 text-right font-mono text-emerald-400">{u.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </section>

          {/* SECCIÓN 3 — Cotizaciones */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">3 · Cotizaciones (detalle)</h3>
            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Cotizaciones por día</CardTitle></CardHeader>
                <CardContent className="pt-0">
                  <div style={{ height: 180 }}>
                    <ResponsiveContainer>
                      <BarChart data={data.quotes.by_day}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                        <XAxis dataKey="date" stroke="#64748b" fontSize={10} />
                        <YAxis stroke="#64748b" fontSize={10} />
                        <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', fontSize: 12 }} />
                        <Bar dataKey="count" fill="#10b981" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Por modalidad</CardTitle></CardHeader>
                <CardContent className="pt-0">
                  {data.quotes.by_modality.length === 0 ? <p className="text-xs text-slate-500">Sin datos.</p> : (
                    <div style={{ height: 180 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={data.quotes.by_modality} dataKey="count" nameKey="modality" outerRadius={65} label={(e) => e.modality}>
                            {data.quotes.by_modality.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-3">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Top 5 regiones</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-1.5">
                  {data.quotes.by_region.length === 0 ? <p className="text-xs text-slate-500">Sin datos.</p> :
                    data.quotes.by_region.map((r, i) => (
                      <div key={i} className="flex justify-between text-xs"><span className="text-slate-300">{r.region}</span><span className="text-emerald-400 font-mono">{r.count}</span></div>
                    ))
                  }
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Por urgencia</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-1.5">
                  {data.quotes.by_urgency.length === 0 ? <p className="text-xs text-slate-500">Sin datos.</p> :
                    data.quotes.by_urgency.map((u, i) => (
                      <div key={i} className="flex justify-between text-xs"><span className="text-slate-300 capitalize">{u.urgency}</span><span className="text-emerald-400 font-mono">{u.count}</span></div>
                    ))
                  }
                </CardContent>
              </Card>
            </div>

            {data.quotes.recent.length > 0 && (
              <Card className="bg-slate-900 border-slate-800 mt-3">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Últimas cotizaciones</CardTitle></CardHeader>
                <CardContent className="pt-0">
                  <div className="max-h-72 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-slate-900"><tr className="text-slate-500 border-b border-slate-800">
                        <th className="text-left p-1.5">Email</th><th className="text-left p-1.5">Modalidad</th><th className="text-left p-1.5">Región</th><th className="text-left p-1.5">Status</th><th className="text-right p-1.5">Centros</th>
                      </tr></thead>
                      <tbody>
                        {data.quotes.recent.map((q, i) => (
                          <tr key={i} className="text-slate-300 border-b border-slate-900">
                            <td className="p-1.5 truncate max-w-[200px]">{q.email}</td><td className="p-1.5 text-slate-400">{q.modality}</td><td className="p-1.5 text-slate-400">{q.region}</td><td className="p-1.5 text-slate-400">{q.status || '—'}</td><td className="p-1.5 text-right text-emerald-400 font-mono">{q.matched_count || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </section>

          {/* SECCIÓN 4 — Contactos */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">4 · WhatsApp · Llamadas · Mapas</h3>
            <div className="grid sm:grid-cols-3 gap-3 mb-3">
              <KPICard k={{ key: 'wsp', label: 'WhatsApp clicks', icon: 'Send', value: data.contact_actions.whatsapp.count, delta: data.contact_actions.whatsapp.delta }} />
              <KPICard k={{ key: 'call', label: 'Llamadas / teléfono', icon: 'Send', value: data.contact_actions.call_phone.count }} />
              <KPICard k={{ key: 'maps', label: 'Click en mapas', icon: 'Send', value: data.contact_actions.maps.count }} />
            </div>
            <div className="grid lg:grid-cols-3 gap-4">
              {[
                { title: 'Top centros · WhatsApp', items: data.contact_actions.wsp_top_centers, base: '/centro/' },
                { title: 'Top profesionales · WhatsApp', items: data.contact_actions.wsp_top_professionals, base: '/profesional/' },
                { title: 'Top talleres · WhatsApp', items: data.contact_actions.wsp_top_workshops, base: '/taller/' },
              ].map((g, gi) => (
                <Card key={gi} className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">{g.title}</CardTitle></CardHeader>
                  <CardContent className="pt-0 space-y-1.5">
                    {g.items.length === 0 ? <p className="text-xs text-slate-500">Sin datos.</p> :
                      g.items.map((t, i) => (
                        <a key={i} href={t.slug ? g.base + t.slug : '#'} target="_blank" rel="noreferrer" className="flex items-center justify-between text-xs hover:bg-slate-800/50 rounded px-1.5 py-1">
                          <span className="text-slate-300 truncate flex items-center gap-1.5">
                            {t.plan_type === 'elite' && <Crown className="w-3 h-3 text-amber-400 shrink-0" />}
                            {t.plan_type === 'premium' && <Star className="w-3 h-3 text-emerald-400 shrink-0" />}
                            <span className="truncate">{t.name}</span>
                          </span>
                          <span className="text-emerald-400 font-mono shrink-0">{t.count}</span>
                        </a>
                      ))
                    }
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* SECCIÓN 5 — Perfiles */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">5 · Centros y profesionales</h3>
            <div className="grid sm:grid-cols-4 gap-3 mb-3">
              <KPICard k={{ key: 'new-c', label: 'Centros nuevos', icon: 'UserPlus', value: data.profiles.new_centers }} />
              <KPICard k={{ key: 'new-p', label: 'Profesionales nuevos', icon: 'UserPlus', value: data.profiles.new_professionals }} />
              <KPICard k={{ key: 'new-w', label: 'Talleres nuevos', icon: 'UserPlus', value: data.profiles.new_workshops }} />
              <KPICard k={{ key: 'new-r', label: `Reviews nuevas (★${data.profiles.avg_rating_period ?? '—'})`, icon: 'CheckCircle2', value: data.profiles.new_reviews }} />
            </div>
            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Logins por role</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-1.5">
                  {data.profiles.logins_by_role.length === 0 ? <p className="text-xs text-slate-500">Sin logins en el período.</p> :
                    data.profiles.logins_by_role.map((l, i) => (
                      <div key={i} className="flex justify-between text-xs"><span className="text-slate-300 capitalize">{l.role}</span><span className="text-emerald-400 font-mono">{l.count}</span></div>
                    ))
                  }
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Centros por plan</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-1.5">
                  {data.profiles.plan_distribution.map((p, i) => (
                    <div key={i} className="flex justify-between text-xs"><span className="text-slate-300 capitalize flex items-center gap-1">{p.plan === 'elite' && <Crown className="w-3 h-3 text-amber-400" />}{p.plan === 'premium' && <Star className="w-3 h-3 text-emerald-400" />}{p.plan}</span><span className="text-emerald-400 font-mono">{p.count}</span></div>
                  ))}
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Top centros visitados</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-1.5">
                  {data.profiles.top_centers_visited.length === 0 ? <p className="text-xs text-slate-500">Sin datos.</p> :
                    data.profiles.top_centers_visited.map((c, i) => (
                      <a key={i} href={c.slug ? `/centro/${c.slug}` : '#'} target="_blank" rel="noreferrer" className="flex items-center justify-between text-xs hover:bg-slate-800/50 rounded px-1.5 py-1">
                        <span className="text-slate-300 truncate flex items-center gap-1.5">
                          {c.plan_type === 'elite' && <Crown className="w-3 h-3 text-amber-400" />}
                          <span className="truncate">{c.name}</span>
                        </span>
                        <span className="text-emerald-400 font-mono shrink-0">{c.count}</span>
                      </a>
                    ))
                  }
                </CardContent>
              </Card>
            </div>
          </section>

          {/* SECCIÓN 6 — Reto 21 + TerapeutIA */}
          <section>
            <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">6 · Herramientas (Reto 21 + TerapeutIA)</h3>
            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Reto 21</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">Iniciados:</span><span className="text-emerald-400 font-mono">{data.tools.reto21.iniciados}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Completados:</span><span className="text-emerald-400 font-mono">{data.tools.reto21.completados}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Completion rate:</span><span className="text-emerald-400 font-mono">{data.tools.reto21.completion_rate}%</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Drip activos hoy:</span><span className="text-emerald-400 font-mono">{data.tools.reto21.drip_activos_hoy}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">PDF descargas:</span><span className="text-emerald-400 font-mono">{data.tools.reto21.pdf_descargas}</span></div>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Sustancia principal</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-1.5">
                  {data.tools.reto21.por_sustancia.length === 0 ? <p className="text-xs text-slate-500">Sin datos.</p> :
                    data.tools.reto21.por_sustancia.slice(0, 8).map((s, i) => (
                      <div key={i} className="flex justify-between text-xs"><span className="text-slate-300 capitalize">{s.sustancia}</span><span className="text-emerald-400 font-mono">{s.count}</span></div>
                    ))
                  }
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">TerapeutIA + Estudio + Boost</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">Sesiones TerapeutIA:</span><span className="text-emerald-400 font-mono">{data.tools.terapeutia.sesiones}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Turnos promedio:</span><span className="text-emerald-400 font-mono">{data.tools.terapeutia.turnos_promedio}</span></div>
                  <div className="flex justify-between border-t border-slate-800 pt-2"><span className="text-slate-400">Estudio PDF descargas:</span><span className="text-emerald-400 font-mono">{data.tools.estudio_pdf_descargas}</span></div>
                  <div className="flex justify-between border-t border-slate-800 pt-2"><span className="text-slate-400">Boosts Reto 21 enviados:</span><span className="text-emerald-400 font-mono">{data.tools.boost?.enviados ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Cotizaciones desde boost:</span><span className="text-emerald-400 font-mono">{data.tools.boost?.cotizaciones_atribuidas ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Boost pendientes (agendados):</span><span className="text-amber-300 font-mono">{data.tools.boost?.pendientes ?? 0}</span></div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* SECCIÓN 7 — El Principio del Mapa Inverso (libro de Nelson) */}
          {data.mapa_inverso && (
            <section data-testid="vamosya-mapa-inverso">
              <h3 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3 flex items-center gap-2">
                <BookOpen className="w-3.5 h-3.5 text-red-400" />
                7 · El Principio del Mapa Inverso · clicks al libro
              </h3>
              <div className="grid sm:grid-cols-4 gap-3 mb-3">
                <KPICard k={{ key: 'mi-impressions', label: 'Impresiones banner', icon: 'Mail', value: data.mapa_inverso.impressions }} />
                <div className="p-4 bg-slate-900 border border-red-500/30 rounded-xl" data-testid="vamosya-kpi-mi-clicks">
                  <div className="flex items-center justify-between mb-2">
                    <BookOpen className="w-4 h-4 text-red-400" />
                    <Delta delta={data.mapa_inverso.clicks_delta} />
                  </div>
                  <div className="text-2xl font-bold text-red-300">{(data.mapa_inverso.clicks || 0).toLocaleString('es-CL')}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">Clicks &quot;Conocer el libro&quot;</div>
                </div>
                <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl" data-testid="vamosya-kpi-mi-ctr">
                  <div className="text-2xl font-bold text-emerald-300">{data.mapa_inverso.ctr ?? '—'}{data.mapa_inverso.ctr !== null && '%'}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">CTR global</div>
                </div>
                <a href="https://elmapainverso.com/" target="_blank" rel="noreferrer" className="p-4 bg-gradient-to-br from-red-900/30 to-slate-900 border border-red-500/30 rounded-xl hover:border-red-400/50 transition-colors flex flex-col justify-center" data-testid="vamosya-mi-book-link">
                  <div className="text-[11px] text-red-300 font-bold tracking-wider uppercase mb-1">Sitio del libro</div>
                  <div className="text-sm text-slate-200 inline-flex items-center gap-1">elmapainverso.com <ExternalLink className="w-3 h-3" /></div>
                </a>
              </div>
              <div className="grid lg:grid-cols-2 gap-4">
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Performance por canal</CardTitle></CardHeader>
                  <CardContent className="pt-0 space-y-2">
                    {data.mapa_inverso.by_channel.length === 0 ? <p className="text-xs text-slate-500">Aún no hay impresiones ni clicks. Los datos aparecerán cuando los emails se envíen y los lectores interactúen.</p> :
                      data.mapa_inverso.by_channel.map((row, i) => (
                        <div key={i} className="border-b border-slate-800/60 last:border-0 pb-1.5 last:pb-0">
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-200 font-medium">{row.channel}</span>
                            <span className="text-red-300 font-mono">{row.clicks} clicks</span>
                          </div>
                          <div className="flex justify-between text-[10px] text-slate-500 mt-0.5">
                            <span>{row.impressions.toLocaleString('es-CL')} impresiones</span>
                            <span className="text-emerald-400">CTR {row.ctr ?? '—'}{row.ctr !== null && '%'}</span>
                          </div>
                        </div>
                      ))
                    }
                  </CardContent>
                </Card>
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-200">Top campañas (granular)</CardTitle></CardHeader>
                  <CardContent className="pt-0 space-y-1.5">
                    {data.mapa_inverso.by_campaign.length === 0 ? <p className="text-xs text-slate-500">Sin clicks en el período.</p> :
                      data.mapa_inverso.by_campaign.slice(0, 12).map((row, i) => (
                        <div key={i} className="flex items-center justify-between text-xs border-b border-slate-800/60 last:border-0 pb-1 last:pb-0">
                          <code className="text-slate-300 text-[11px] truncate">{row.campaign}</code>
                          <span className="text-slate-500 text-[10px] shrink-0 ml-2">
                            <span className="text-emerald-400 font-mono">{row.clicks}</span> / {row.impressions.toLocaleString('es-CL')} · <span className="text-emerald-300">{row.ctr ?? '—'}{row.ctr !== null && '%'}</span>
                          </span>
                        </div>
                      ))
                    }
                  </CardContent>
                </Card>
              </div>
            </section>
          )}

          <p className="text-[10px] text-slate-600 text-center pt-2">Última actualización: {new Date(data.generated_at).toLocaleString('es-CL')} · Periodo: {data.range.start.slice(0,10)} → {data.range.end.slice(0,10)}</p>
        </>
      )}
    </div>
  );
}
